import { useState, useEffect, useRef } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { Card, Space, Button, Tag, Typography, Modal, notification, Tooltip, Avatar } from 'antd';
import {
	EditOutlined,
	DeleteOutlined,
	InfoCircleOutlined,
	WarningOutlined,
	CalendarOutlined,
	LikeOutlined,
	DislikeOutlined,
	EyeFilled,
} from '@ant-design/icons';
import VirtualList from 'rc-virtual-list';
import { CRMGroup, CRMLead } from '@/types/TCRM';
import CRMGroupCreator, { CRMGroupCreatorRef } from './crmGroupCreator';
import { deleteAGroup, updateAGroup } from '@/api/crm/groups';
import { useMutation } from '@tanstack/react-query';
import { deleteCRMLead, updateCRMLead, updateLeadGroup } from '@/api/crm/leads';
import CRMLeadCreator, { CRMLeadCreatorRef } from './crmLeadCreator';
import { safeJSONParse } from '@/utils/helper';

const { Text } = Typography;
import { TCallTransferWorkflow, TTextingWorkflow } from '@/types/TWorkflows';
import { User } from '@/types/TAccounts';

type BoardViewProps = {
	clientID: string;
	clientData: User;
	leads: CRMLead[];
	groups: CRMGroup[];
	handleViewLead: (lead: CRMLead) => void;
	refetchAllLeads: () => void;
	refetchCRMGroups: () => void;
	refetchLead: (leadID: string) => void;
};

const BoardView: React.FC<BoardViewProps> = ({
	clientID,
	clientData,
	leads,
	groups,
	refetchCRMGroups,
	refetchAllLeads,
	refetchLead,
	handleViewLead,
}) => {
	const [leadsState, setLeadsState] = useState<CRMLead[]>(leads ?? []);
	const [selectedGroup, setSelectedGroup] = useState<CRMGroup | null>(null);
	const [editGroupModal, setEditGroupModal] = useState<boolean>(false);
	const [editLeadModal, setEditLeadModal] = useState<CRMLead | null>(null);
	const [deleteLeadId, setDeleteLeadId] = useState<string | null>(null);
	const [deleteGroupId, setDeleteGroupId] = useState<string | null>(null);
	const formRef = useRef<CRMGroupCreatorRef>(null);
	const leadFormRef = useRef<CRMLeadCreatorRef>(null);

	const [textingWorkflow, setTextingWorkflow] = useState<TTextingWorkflow[]>([]);
	const [callTransferWorkflow, setCallTransferWorkflow] = useState<TCallTransferWorkflow[]>([]);

	useEffect(() => {
		try {
			// Parse texting workflows with proper validation
			let parsedTextingWorkflows: TTextingWorkflow[] = [];
			let parsedCallWorkflows: TCallTransferWorkflow[] = [];

			// Handle texting workflows
			if (clientData?.workflows) {
				if (typeof clientData.workflows === 'string') {
					try {
						const parsed = safeJSONParse(clientData.workflows, []);
						const workflowArray = Array.isArray(parsed) ? parsed : [parsed];
						parsedTextingWorkflows = workflowArray
							.filter((w: any) => w && typeof w === 'object' && w.id && w.textMessage !== undefined)
							.map(
								(w: any): TTextingWorkflow => ({
									id: w.id,
									description: w.description || 'Unnamed Workflow',
									textMessage: w.textMessage || '',
									voiceResponse: w.voiceResponse || '',
									askForPermissionOnText: w.askForPermissionOnText,
									customTextFooter: w.customTextFooter,
								}),
							);
					} catch (error) {
						console.error('Failed to parse texting workflows:', error);
					}
				} else if (Array.isArray(clientData.workflows)) {
					parsedTextingWorkflows = clientData.workflows
						.filter((w: any) => w && typeof w === 'object' && w.id && w.textMessage !== undefined)
						.map(
							(w: any): TTextingWorkflow => ({
								id: w.id,
								description: w.description || 'Unnamed Workflow',
								textMessage: w.textMessage || '',
								voiceResponse: w.voiceResponse || '',
								askForPermissionOnText: w.askForPermissionOnText,
								customTextFooter: w.customTextFooter,
							}),
						);
				}
			}

			// Handle call transfer workflows
			if (clientData?.callWorkflows) {
				if (typeof clientData.callWorkflows === 'string') {
					try {
						const parsed = safeJSONParse(clientData.callWorkflows, []);
						const workflowArray = Array.isArray(parsed) ? parsed : [parsed];
						parsedCallWorkflows = workflowArray
							.filter(
								(w: any) => w && typeof w === 'object' && w.workflowForwardingNumber !== undefined,
							)
							.map(
								(w: any, index: number): TCallTransferWorkflow => ({
									id: w.id || `call-workflow-${index}`,
									description: w.description || 'Unnamed Call Workflow',
									workflowForwardingNumber: String(w.workflowForwardingNumber),
									workflowForwardingDID: w.workflowForwardingDID,
									voiceResponse: w.voiceResponse,
								}),
							);
					} catch (error) {
						console.error('Failed to parse call transfer workflows:', error);
					}
				} else if (Array.isArray(clientData.callWorkflows)) {
					parsedCallWorkflows = clientData.callWorkflows
						.filter(
							(w: any) => w && typeof w === 'object' && w.workflowForwardingNumber !== undefined,
						)
						.map(
							(w: any, index: number): TCallTransferWorkflow => ({
								id: w.id || `call-workflow-${index}`,
								description: w.description || 'Unnamed Call Workflow',
								workflowForwardingNumber: String(w.workflowForwardingNumber),
								workflowForwardingDID: w.workflowForwardingDID,
								voiceResponse: w.voiceResponse,
							}),
						);
				}
			}

			setTextingWorkflow(parsedTextingWorkflows);
			setCallTransferWorkflow(parsedCallWorkflows);

			console.log('Parsed texting workflows:', parsedTextingWorkflows);
			console.log('Parsed call transfer workflows:', parsedCallWorkflows);
		} catch (error) {
			console.error('Unable to parse the workflow', error);
			setTextingWorkflow([]);
			setCallTransferWorkflow([]);
		}
	}, [clientData]);

	const isWorkflowValid = (group: CRMGroup) => {
		if (group.trigger_type !== 'workflow') {
			return true;
		}

		const workflowId = group.trigger_details?.workflow?.workflow_id;
		if (!workflowId) {
			return false;
		}

		// Ensure arrays are properly initialized
		const textingWorkflows = Array.isArray(textingWorkflow) ? textingWorkflow : [];
		const callWorkflows = Array.isArray(callTransferWorkflow) ? callTransferWorkflow : [];

		return (
			textingWorkflows.some((w) => w.id === workflowId) ||
			callWorkflows.some((w) => w.id === workflowId)
		);
	};

	const updateLeadGroupMutation = useMutation({
		mutationFn: updateLeadGroup,
		onSuccess: (_, variables) => {
			notification.success({
				message: 'Lead Moved',
				description: 'The lead has been successfully moved to the new group.',
			});
			refetchLead(variables.leadID);
		},
		onError: (_, variables) => {
			notification.error({
				message: 'Error',
				description: 'An error occurred while moving the lead.',
			});
			refetchLead(variables.leadID);
		},
	});

	const editCrmGroupMutation = useMutation({
		mutationFn: updateAGroup,
		onSuccess: () => {
			notification.success({
				message: 'Group Updated',
				description: 'The group has been successfully updated.',
			});
			refetchCRMGroups();
			setEditGroupModal(false);
			formRef.current?.resetForm();
		},
		onError: () => {
			notification.error({
				message: 'Error',
				description: 'An error occurred while updating the group.',
			});
		},
	});

	const updateLeadMutation = useMutation({
		mutationFn: updateCRMLead,
		onSuccess: () => {
			notification.success({
				message: 'Lead Updated',
				description: 'The lead has been successfully updated.',
			});
			setEditLeadModal(null);
			refetchLead(editLeadModal?.lead_id as string);
		},
		onError: () => {
			notification.error({
				message: 'Error',
				description: 'An error occurred while updating the lead.',
			});
		},
	});

	const deleteLeadMutation = useMutation({
		mutationFn: deleteCRMLead,
		onSuccess: () => {
			notification.success({
				message: 'Lead Deleted',
				description: 'The lead has been successfully deleted.',
			});
			setDeleteLeadId(null);
			refetchAllLeads();
		},
		onError: () => {
			notification.error({
				message: 'Error',
				description: 'An error occurred while deleting the lead.',
			});
		},
	});

	const deleteGroupMutation = useMutation({
		mutationFn: deleteAGroup,
		onSuccess: () => {
			notification.success({
				message: 'Group Deleted',
				description: 'The group has been successfully deleted.',
			});
			setDeleteGroupId(null);
			refetchCRMGroups();
		},
		onError: (error) => {
			notification.error({
				message: 'Error',
				description: error.message,
			});
		},
	});

	const handleUpdateGroup = (values: CRMGroup) => {
		editCrmGroupMutation.mutate({
			clientID,
			groupID: selectedGroup?.group_id as string,
			groupData: values,
		});
	};

	const handleUpdateLead = (values: Partial<CRMLead>) => {
		updateLeadMutation.mutate({ clientID, leadID: values.lead_id as string, leadData: values });
	};

	const handleDeleteLead = () => {
		deleteLeadMutation.mutate({ clientID, leadID: deleteLeadId as string });
	};

	const handleDragEnd = (result: DropResult) => {
		const { source, destination, draggableId } = result;

		if (
			!destination ||
			(source.droppableId === destination.droppableId && source.index === destination.index)
		) {
			return;
		}

		const targetGroup = groups.find((group) => group.group_id === destination.droppableId);
		if (targetGroup && !isWorkflowValid(targetGroup)) {
			notification.error({
				message: 'Invalid Move',
				description:
					'This group has an invalid workflow configuration. Please fix the workflow before moving leads here.',
			});
			return;
		}

		const draggedLead = leadsState.find((lead) => lead.lead_id === draggableId);
		if (!draggedLead) {
			return;
		}

		const newLeadsState = leadsState.map((lead) =>
			lead.lead_id === draggableId ? { ...lead, group_id: destination.droppableId } : lead,
		);
		setLeadsState(newLeadsState);

		updateLeadGroupMutation.mutate({
			leadID: draggableId,
			groupID: destination.droppableId,
		});
	};

	useEffect(() => {
		setLeadsState(leads ?? []);
	}, [leads]);

	const getColumnColor = (groupName: string) => {
		const lowerName = groupName.toLowerCase();
		if (lowerName === 'interested') {
			return '#4CAF50';
		}
		if (lowerName === 'not interested') {
			return '#F44336';
		}
		if (lowerName === 'meeting scheduled') {
			return '#7E57C2';
		}
		return '#9E9E9E'; // Default color
	};

	const getEmptyStateIcon = (groupName: string) => {
		const lowerName = groupName.toLowerCase();

		if (lowerName === 'meeting schedule') {
			return (
				<>
					<Avatar
						style={{ backgroundColor: 'rgba(126, 87, 194, .2)', verticalAlign: 'middle' }}
						size={64}
					>
						<CalendarOutlined style={{ fontSize: 32, color: '#7E57C2' }} />
					</Avatar>
				</>
			);
		}

		if (lowerName === 'interested') {
			return (
				<>
					<Avatar
						style={{ backgroundColor: 'rgba(132, 223, 135, 0.2)', verticalAlign: 'middle' }}
						size={64}
					>
						<LikeOutlined style={{ fontSize: 32, color: '#4CAF50' }} />
					</Avatar>
				</>
			);
		}

		if (lowerName === 'not interested') {
			return (
				<>
					<Avatar
						style={{ backgroundColor: 'rgba(244, 67, 54, .2)', verticalAlign: 'middle' }}
						size={64}
					>
						<DislikeOutlined style={{ fontSize: 32, color: '#F44336' }} />
					</Avatar>
				</>
			);
		}

		return (
			<>
				<Avatar
					style={{ backgroundColor: 'rgba(232, 234, 246, .2)', verticalAlign: 'middle' }}
					size={64}
				>
					<InfoCircleOutlined style={{ fontSize: 32, color: 'gray' }} />
				</Avatar>
			</>
		);
	};

	// Generate initials from name
	const getInitials = (name: string) => {
		if (!name) {
			return '?';
		}
		const parts = name.split(' ');
		if (parts.length === 1) {
			return parts[0].charAt(0).toUpperCase();
		}
		return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
	};

	// Generate avatar background color from name
	const getAvatarColor = (name: string) => {
		if (!name) {
			return '#9E9E9E';
		}
		const colors = [
			'#F44336',
			'#E91E63',
			'#9C27B0',
			'#673AB7',
			'#3F51B5',
			'#2196F3',
			'#03A9F4',
			'#00BCD4',
			'#009688',
			'#4CAF50',
			'#8BC34A',
			'#CDDC39',
			'#FFC107',
			'#FF9800',
			'#FF5722',
		];
		let hash = 0;
		for (let i = 0; i < name.length; i++) {
			hash = name.charCodeAt(i) + ((hash << 5) - hash);
		}
		hash = Math.abs(hash);
		return colors[hash % colors.length];
	};

	// Format date for display
	const formatDate = (dateString: string) => {
		if (!dateString) {
			return '';
		}
		const date = new Date(dateString);
		const now = new Date();
		const diff = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

		if (diff === 0) {
			return 'Today';
		}
		if (diff === 1) {
			return 'Yesterday';
		}
		if (diff < 7) {
			return `${diff} days ago`;
		}

		const monthNames = [
			'Jan',
			'Feb',
			'Mar',
			'Apr',
			'May',
			'Jun',
			'Jul',
			'Aug',
			'Sep',
			'Oct',
			'Nov',
			'Dec',
		];
		return `${monthNames[date.getMonth()]} ${date.getDate()}`;
	};

	const renderLeadCard = (lead: CRMLead, index: number) => {
		const name = lead.dynamic_fields?.name || lead.phone_number || lead.email || 'Unknown';
		const company = lead.dynamic_fields?.company_name || '';
		const date = lead?.createdAt;
		if (lead.isEmptyPlaceholder) {
			return (
				<div className="flex flex-col items-center justify-center py-8 px-4 text-center border-dashed border rounded-xl bg-gray-50">
					{lead.emptyIcon}
					<Text className="text-lg font-medium text-gray-400 mt-4">{lead.emptyTitle}</Text>
					<Text className="text-sm text-gray-400 mt-2">{lead.emptyDescription}</Text>
				</div>
			);
		}

		return (
			<Draggable key={lead.lead_id} draggableId={lead.lead_id} index={index}>
				{(provided, snapshot) => (
					<div
						ref={provided.innerRef}
						{...provided.draggableProps}
						{...provided.dragHandleProps}
						style={{
							...provided.draggableProps.style,
							opacity: snapshot.isDragging ? 0.8 : 1,
						}}
					>
						<Card
							size="small"
							className="mb-3 hover:shadow-md transition-all duration-200"
							style={{
								borderRadius: '8px',
								boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
							}}
						>
							<div className="space-y-2">
								<div className="flex items-center">
									<Avatar
										style={{
											backgroundColor: getAvatarColor(name),
											marginRight: '12px',
										}}
										size="large"
									>
										{getInitials(name)}
									</Avatar>
									<div className="flex-1">
										<div className="flex justify-between items-center">
											<div>
												<Text strong className="text-base block">
													{name}
												</Text>
												{company && <Text className="text-xs text-gray-500 block">{company}</Text>}
											</div>
										</div>
									</div>
								</div>

								<div className="flex items-center justify-between">
									<div className="flex items-center">
										<Tag color="orange" className="mr-2 border-0 text-xs rounded-sm px-2">
											{lead.lead_type || 'Manual'}
										</Tag>
										<Text className="text-xs text-gray-400">Added {formatDate(date)}</Text>
									</div>

									<div className="flex items-center gap-1">
										<Button
											type="text"
											size="small"
											className="rounded-full flex items-center justify-center w-7 h-7 hover:bg-gray-100"
											icon={<EditOutlined className="text-blue-500 text-xs" />}
											onClick={() => setEditLeadModal(lead)}
										/>
										<Button
											type="text"
											size="small"
											className="rounded-full flex items-center justify-center w-7 h-7 hover:bg-gray-100"
											icon={<DeleteOutlined className="text-red-500 text-xs" />}
											onClick={() => setDeleteLeadId(lead.lead_id)}
										/>
										<Button
											type="text"
											size="small"
											className="rounded-full flex items-center justify-center w-7 h-7 hover:bg-gray-100"
											onClick={() => handleViewLead(lead)}
											icon={<EyeFilled className="text-blue-600 text-xs" />}
										/>
									</div>
								</div>
							</div>
						</Card>
					</div>
				)}
			</Draggable>
		);
	};

	const renderEmptyState = (group: CRMGroup) => {
		const lowerName = group.group_name.toLowerCase();
		let title = 'No leads yet';
		let description = 'Leads will appear here when added';

		if (lowerName === 'interested') {
			title = 'No Interested Leads Yet';
			description =
				'AI will automatically place leads here when it detects positive signals in conversations';
		} else if (lowerName === 'not interested') {
			title = 'No Not Interested Leads Yet';
			description =
				'AI will place leads here when it detects not interested signals in conversations';
		} else if (lowerName === 'meeting scheduled') {
			title = 'No meetings scheduled';
			description =
				'AI will place leads here when it detects meeting arrangements in conversations';
		}

		return {
			lead_id: `empty-${group.group_id}`,
			group_id: group.group_id,
			isEmptyPlaceholder: true,
			emptyIcon: getEmptyStateIcon(group.group_name),
			emptyTitle: title,
			emptyDescription: description,
		};
	};

	const getGroupLeads = (groupId: string) => {
		const groupLeads = leadsState.filter((lead) => lead.group_id === groupId);

		// If no leads, return placeholder
		if (groupLeads.length === 0) {
			const group = groups.find((g) => g.group_id === groupId);
			if (group) {
				return [renderEmptyState(group) as any];
			}
		}

		return groupLeads;
	};

	const handleEditGroup = (group: CRMGroup) => {
		setSelectedGroup(group);
		setEditGroupModal(true);
	};

	return (
		<div className="h-full">
			<DragDropContext onDragEnd={handleDragEnd}>
				<div className="bg-gray-50 overflow-x-auto overflow-y-hidden h-full py-6">
					<div className="min-w-[1024px] h-full px-6">
						<div className="grid grid-cols-3 gap-6 h-full">
							{groups?.map((group) => {
								const isValid = isWorkflowValid(group);
								const columnColor = getColumnColor(group.group_name);
								const groupLeads = getGroupLeads(group.group_id);

								return (
									<Droppable
										key={group.group_id}
										droppableId={group.group_id}
										isDropDisabled={!isValid}
									>
										{(provided, snapshot) => (
											<div
												ref={provided.innerRef}
												{...provided.droppableProps}
												className={`flex flex-col h-full rounded-lg overflow-hidden bg-white shadow-sm ${
													!isValid ? 'opacity-90' : ''
												}`}
												style={{ border: '1px solid #f0f0f0' }}
											>
												<div
													className="px-4 py-3 flex items-center justify-between border-b border-gray-100"
													style={{ borderLeft: `4px solid ${columnColor}` }}
												>
													<div className="flex flex-col">
														<Text strong className="text-base flex items-center gap-1">
															{group.group_name}
															{!isValid && (
																<Tooltip title="This group's workflow configuration is invalid. The specified workflow doesn't exist.">
																	<WarningOutlined className="text-red-500 text-sm" />
																</Tooltip>
															)}
														</Text>
														<Text className="text-xs text-gray-400">
															{group.trigger_type === 'workflow' ? 'AI detected ' : ''}
															{group.group_name.toLowerCase().includes('interested')
																? 'interest'
																: group.group_name.toLowerCase().includes('not interested')
																	? 'rejection'
																	: group.group_name.toLowerCase().includes('meeting')
																		? 'scheduling'
																		: ''}
														</Text>
													</div>
													<div className="flex items-center gap-2">
														<Tag className="rounded-full text-lg font-medium px-3 py-1">
															{leadsState.filter((lead) => lead.group_id === group.group_id).length}
														</Tag>
														<Button
															type="text"
															size="small"
															className="rounded-full flex items-center justify-center w-7 h-7 hover:bg-gray-100"
															icon={<EditOutlined className="text-blue-500 text-xs" />}
															onClick={() => handleEditGroup(group)}
														/>
														<Button
															type="text"
															size="small"
															className="rounded-full flex items-center justify-center w-7 h-7 hover:bg-gray-100"
															icon={<DeleteOutlined className="text-red-500 text-xs" />}
															onClick={() => setDeleteGroupId(group.group_id)}
														/>
													</div>
												</div>

												<div
													className={`flex-1 overflow-y-auto p-4 ${
														!isValid ? 'border-2 border-red-200 bg-red-50/30' : ''
													}`}
													style={{
														backgroundColor: snapshot.isDraggingOver
															? isValid
																? '#f9fafc'
																: '#fee2e2'
															: !isValid
																? '#fff1f2'
																: 'white',
													}}
												>
													<VirtualList
														height={450}
														itemHeight={120}
														itemKey="lead_id"
														data={groupLeads}
													>
														{(lead, index) => renderLeadCard(lead, index)}
													</VirtualList>
													{provided.placeholder}
												</div>
											</div>
										)}
									</Droppable>
								);
							})}
						</div>
					</div>
				</div>
			</DragDropContext>

			<Modal
				open={editGroupModal}
				onCancel={() => {
					setEditGroupModal(false);
					setSelectedGroup(null);
				}}
				footer={null}
				width={800}
				centered
			>
				{selectedGroup && (
					<CRMGroupCreator
						ref={formRef}
						initialData={selectedGroup}
						onSubmit={handleUpdateGroup}
						isLoading={editCrmGroupMutation.isPending}
						clientData={clientData}
					/>
				)}
			</Modal>

			<Modal
				open={!!editLeadModal}
				footer={null}
				onCancel={() => setEditLeadModal(null)}
				width={900}
				centered
			>
				<CRMLeadCreator
					ref={leadFormRef}
					initialData={editLeadModal as CRMLead}
					onSubmit={handleUpdateLead}
					isLoading={updateLeadMutation.isPending}
					groups={groups}
				/>
			</Modal>

			<Modal
				open={!!deleteLeadId}
				onCancel={() => setDeleteLeadId(null)}
				footer={
					<Space size="small">
						<Button size="small" onClick={() => setDeleteLeadId(null)}>
							Cancel
						</Button>
						<Button
							size="small"
							danger
							loading={deleteLeadMutation.isPending}
							onClick={handleDeleteLead}
						>
							Delete
						</Button>
					</Space>
				}
				width={400}
			>
				<Text className="text-sm">Are you sure you want to delete this lead?</Text>
			</Modal>

			<Modal
				open={!!deleteGroupId}
				onCancel={() => setDeleteGroupId(null)}
				footer={
					<Space size="small">
						<Button size="small" onClick={() => setDeleteGroupId(null)}>
							Cancel
						</Button>
						<Button
							size="small"
							danger
							loading={deleteGroupMutation.isPending}
							onClick={() => {
								deleteGroupMutation.mutate({ clientID, groupID: deleteGroupId as string });
							}}
						>
							Delete
						</Button>
					</Space>
				}
				width={400}
			>
				<Text className="text-sm">Are you sure you want to delete this group?</Text>
			</Modal>
		</div>
	);
};

export default BoardView;
